import React, { useState, useEffect } from "react";
import QueryTextInput from "./QueryTextInput";
import QueryModelTypeSelect from "./QueryModelTypeSelect";
import QueryModelEmbeddingTypeSelect from "./QueryModelEmbeddingTypeSelect";
import QueryModelIdInput from "./QueryModelIdInput";
import SearchModelTypesCheckboxes from "./SearchModelTypesCheckboxes";
import EmbeddingTypesCheckboxes from "./EmbeddingTypesCheckboxes";
import WithinDistanceInput from "./WithinDistanceInput";
import OrderBySelect from "./OrderBySelect";
import DistanceTypeSelect from "./DistanceTypeSelect";
import ModelTimestampWithinHoursInput from "./ModelTimestampWithinHoursInput";
import LimitInput from "./LimitInput";
import SearchVectorColumnSelect from "./SearchVectorColumnSelect";

function AdvancedForm({ activeTab, onSearch, fieldErrors, searchActive }) {
  const [formValues, setFormValues] = useState({
    query: "",
    queryModelType: "",
    queryModelEmbeddingType: "MatchFeed::AggregatedEmbedding",
    queryModelId: "",
    searchModelTypes: ["Id::PromoEntity::Expert"],
    embeddingTypes: ["MatchFeed::Embedding", "MatchFeed::AggregatedEmbedding"],
    withinDistance: "",
    orderBy: "distance_asc",
    modelTimestampWithinHours: "",
    limit: "30",
    searchVectorColumn: "text_embedding_3_large_256",
    distanceType: "cosine",
  });

  const camelToUnderscore = (str) => str.replace(/([A-Z])/g, "_$1").toLowerCase();

  // Overrides form defaults with params parsed from the URL, if present
  // Run-once on mount: reads URL params to initialize form state.
  // Intentionally omits formValues from deps - we want initial values, not to re-run on every form change.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    setFormValues((prevFormValues) => {
      const updatedFormValues = {};

      Object.keys(prevFormValues).forEach((key) => {
        const underscoreKey = camelToUnderscore(key);
        const urlValue = urlParams.get(underscoreKey);
        if (urlValue !== null) {
          updatedFormValues[key] = Array.isArray(prevFormValues[key]) ? urlValue.split(",") : urlValue;
        }
      });

      return { ...prevFormValues, ...updatedFormValues };
    });
  }, []);

  const handleChange = (field, value) => {
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      [field]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSearch(formValues);
  };

  return (
    <form onSubmit={handleSubmit} className="mb-3">
      {activeTab === "query" && (
        <QueryTextInput
          value={formValues.query}
          onChange={(value) => handleChange("query", value)}
          error={fieldErrors.query}
        />
      )}
      {activeTab === "model" && (
        <>
          <QueryModelTypeSelect
            value={formValues.queryModelType}
            onChange={(value) => handleChange("queryModelType", value)}
            error={fieldErrors.query_model_type}
          />
          <QueryModelEmbeddingTypeSelect
            value={formValues.queryModelEmbeddingType}
            onChange={(value) => handleChange("queryModelEmbeddingType", value)}
            error={fieldErrors.query_model_embedding_type}
          />
          <QueryModelIdInput
            value={formValues.queryModelId}
            onChange={(value) => handleChange("queryModelId", value)}
            error={fieldErrors.query_model_id}
          />
        </>
      )}
      <SearchModelTypesCheckboxes
        value={formValues.searchModelTypes}
        onChange={(value) => handleChange("searchModelTypes", value)}
        error={fieldErrors.search_model_types}
      />
      <EmbeddingTypesCheckboxes
        value={formValues.embeddingTypes}
        onChange={(value) => handleChange("embeddingTypes", value)}
        error={fieldErrors.embedding_types}
      />
      <WithinDistanceInput
        value={formValues.withinDistance}
        onChange={(value) => handleChange("withinDistance", value)}
        error={fieldErrors.within_distance}
      />
      <OrderBySelect
        value={formValues.orderBy}
        onChange={(value) => handleChange("orderBy", value)}
        error={fieldErrors.order_by}
      />
      <DistanceTypeSelect
        value={formValues.distanceType}
        onChange={(value) => handleChange("distanceType", value)}
        error={fieldErrors.distance_type}
      />
      <ModelTimestampWithinHoursInput
        value={formValues.modelTimestampWithinHours}
        onChange={(value) => handleChange("modelTimestampWithinHours", value)}
        error={fieldErrors.model_timestamp_within_hours}
      />
      <LimitInput
        value={formValues.limit}
        onChange={(value) => handleChange("limit", value)}
        error={fieldErrors.limit}
      />
      <SearchVectorColumnSelect
        value={formValues.searchVectorColumn}
        onChange={(value) => handleChange("searchVectorColumn", value)}
        error={fieldErrors.search_vector_column}
      />
      <button type="submit" className="btn btn-primary" disabled={searchActive}>
        Search
      </button>
    </form>
  );
}

export default AdvancedForm;
